// This class is for one-offs that formDependencies.js doesn't handle.  It needs:
//
// - an independent select ID
// - a dependent select ID
// - a URL to fetch to get the dependency from the backend
// - any extra parameters that need to be passed to the URL; the independent select's value will always be submitted as 'id'
//
// The URL should always return a JSON that is of the form [ { text: '...', value: '...' }, { ... } ]

import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = {
    independentId: String,
    independentName: String,
    dependentId: String,
    dependentName: String,
    url: String,
    params: Object,
    selected: Array
  };

  connect() {
    this.independentSelect = document.getElementById(this.independentIdValue);
    if (!this.independentSelect) {
      this.independentSelect = document.querySelector(`[name="${this.independentNameValue}"]`)
    }

    this.dependentSelect = document.getElementById(this.dependentIdValue);
    if (!this.dependentSelect) {
      this.dependentSelect = document.querySelector(`select[name="${this.dependentNameValue}"]`)
    }

    this.setupInitialState();
    this.bindEvents();
  }

  disconnect() {
    this.unbindEvents();
  }

  setupInitialState() {
    if (this.independentSelect.value) {
      this.updateDependentOptions();
    } else {
      this.clearDependentSelect();
    }
  }

  bindEvents() {
    this.independentChangeHandler = this.handleIndependentChange.bind(this);
    this.independentSelect.addEventListener("change", this.independentChangeHandler);
  }

  unbindEvents() {
    if (this.independentChangeHandler) {
      this.independentSelect.removeEventListener("change", this.independentChangeHandler);
    }
  }

  // event.target.value should be an integer or a string, not an object
  handleIndependentChange(event) {
    const selectedValue = event.target.value;

    if (selectedValue) {
      this.updateDependentOptions(selectedValue);
    } else {
      this.clearDependentSelect();
    }
  }

  async updateDependentOptions(selectedValue = this.independentSelect.value) {
    const url = this.buildRequestUrl(selectedValue);

    const response = await fetch(url, {
      headers: {
        "Accept": "application/json",
        "X-Requested-With": "XMLHttpRequest"
      }
    });

    const data = await response.json();
    this.populateDependentSelect(data);
    this.setDependentSelectedValues();
  }

  buildRequestUrl(id) {
    const url = new URL(this.urlValue, window.location.origin);

    // Add the independent select value as a parameter
    url.searchParams.set('id', id);

    // Add any additional parameters
    if (this.hasParamsValue) {
      Object.entries(this.paramsValue).forEach(([key, value]) => {
        url.searchParams.set(key, value);
      });
    }

    return url.toString();
  }

  getIndependentParamName() {
    // Use the name attribute of the independent select, or fall back to 'id'
    return this.independentSelect.name || 'id';
  }

  clearDependentSelect() {
    if (this.dependentSelect.matches('select')) {
      this.dependentSelect.innerHTML = ''

      const emptyOption = new Option("", "", true, true);
      this.dependentSelect.add(emptyOption);
    } else if (this.dependentSelect.matches('.multiselect')) {
      const optionsHash = []
      this.dependentSelect.dispatchEvent(new CustomEvent('setOptions', { detail: optionsHash, bubbles: true }))
    }

    // Trigger change event to notify other listeners
    this.dependentSelect.dispatchEvent(new Event("change", { bubbles: true }));
  }

  populateDependentSelect(data) {
    if (this.dependentSelect.matches('select')) {
      // Expect data in format: [ { text: '...', value: '...' }, { ... } ]
      data?.forEach(option => {
        const optionElement = new Option(option.text, option.value, false, false);
        this.dependentSelect.add(optionElement);
      });
    } else if (this.dependentSelect.matches('.multiselect')) {
      this.dependentSelect.dispatchEvent(new CustomEvent('setOptions', { detail: data, bubbles: true }))
    }

    // Trigger change event
    this.dependentSelect.dispatchEvent(new Event("change", { bubbles: true }));
  }

  setDependentSelectedValues() {
    if (this.dependentSelect.matches('select')) {
      this.dependentSelect.value = this.selectedValue[0]
    } else if (this.dependentSelect.matches('.multiselect')) {
      this.dependentSelect.dispatchEvent(new CustomEvent('update:modelValue', { detail: this.selectedValue, bubbles: true }))
    }
  }
}
